import { useState } from "react";
import { ScrollView } from "react-native";
import { graphql, useFragment, useMutation } from "react-relay";
import {
  AHIcon,
  AHText,
  AHTextFonts,
  ColorType,
  TouchableBox,
  useCircleSize,
  useLayoutContext,
  useTheme,
} from "arkh";
import Color from "color";
import { TeamHeaderRoles_team$key } from "./__generated__/TeamHeaderRoles_team.graphql";
import { TeamHeaderRolesAssignMutation } from "./__generated__/TeamHeaderRolesAssignMutation.graphql";
import {
  OverlappingEmployeeAvatars,
  prioritizeRoles,
} from "widgets/src/TodoListItem/TodoListItemFooter/OverlappingEmployeeAvatars";
import { EmployeeRoleEditEmployeesDialog } from "@/src/features/EmployeeRoles/EmployeeRoleEdit/EmployeeRoleEditEmployeesDialog";
import { usePersistedEmployeeId } from "@/src/OS/Login/usePersistedEmployeeId";

export function TeamHeaderRoles(props: {
  team: TeamHeaderRoles_team$key;
  tintColor: ColorType;
}) {
  const team = useFragment(
    graphql`
      fragment TeamHeaderRoles_team on Team {
        id
        roles {
          id
          name
          employeeCount
          employees {
            id
            isLinkedToViewer
            workStatus
            ...Avatar_employee
          }
        }
      }
    `,
    props.team
  );

  const { currentEmployeeId } = usePersistedEmployeeId();
  const [selectedRoleId, setSelectedRoleId] = useState<string | null>(null);

  const [assignEmployees] = useMutation<TeamHeaderRolesAssignMutation>(graphql`
    mutation TeamHeaderRolesAssignMutation($roleId: ID!, $employeeIds: [ID!]!) {
      assignEmployeesToRole(roleId: $roleId, employeeIds: $employeeIds) {
        role {
          id
          name
          employeeCount
          employees {
            id
            isLinkedToViewer
            workStatus
            ...Avatar_employee
          }
        }
      }
    }
  `);

  const roles = team.roles.filter((r) => r != null);
  const sortedRoles = prioritizeRoles(roles, currentEmployeeId ?? undefined);

  const selectedRole = roles.find((r) => r.id === selectedRoleId);

  const layout = useLayoutContext();
  const theme = useTheme();
  const iconColor = Color(theme[props.tintColor]).alpha(0.4).string();
  const avatarSize = useCircleSize("smallMedium");

  if (roles.length === 0) {
    return null;
  }

  return (
    <>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        style={{ height: avatarSize + layout.space.medium * 2 }}
        contentContainerStyle={{
          paddingHorizontal: layout.space.medium,
          gap: layout.space.medium,
          alignItems: "center",
        }}
      >
        {sortedRoles.map((role) => (
          <TouchableBox
            key={role.id}
            onPress={() => setSelectedRoleId(role.id)}
            style={{
              flexDirection: "row",
              alignItems: "center",
              minHeight: avatarSize,
            }}
          >
            {role.employees.length > 0 ? (
              <OverlappingEmployeeAvatars
                employees={role.employees}
                highlightViewer={true}
                currentEmployeeId={currentEmployeeId ?? undefined}
              />
            ) : (
              <AHIcon type="employee-role" customColor={iconColor} />
            )}
            <AHText
              type="body"
              font={AHTextFonts.MEDIUM}
              color={props.tintColor}
              style={{ marginLeft: 6 }}
              numberOfLines={1}
            >
              {role.name}
            </AHText>
          </TouchableBox>
        ))}
      </ScrollView>
      <EmployeeRoleEditEmployeesDialog
        visible={selectedRoleId !== null}
        onClose={() => setSelectedRoleId(null)}
        assignEmployeesIds={selectedRole?.employees.map((e) => e.id) ?? []}
        setAssignEmployeesIds={(employeeIds) => {
          if (selectedRoleId) {
            assignEmployees({
              variables: {
                roleId: selectedRoleId,
                employeeIds,
              },
            });
          }
        }}
        teamId={team.id}
        title={selectedRole?.name}
      />
    </>
  );
}
